import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Autocomplete,
  Divider,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  LinearProgress
} from '@mui/material';
import {
  ShoppingCart as CartIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckIcon,
  PointOfSale as SellIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  RemoveShoppingCart as EmptyCartIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { inventoryService } from '../services/InventoryService';

const QuickSell = ({ shopName }) => {
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [sellingPrice, setSellingPrice] = useState('');
  const [cart, setCart] = useState([]);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      const allItems = await inventoryService.getAllItems(shopName);
      const availableItems = allItems.filter(item => item.quantity > 0);
      setItems(availableItems);
    } catch (error) {
      showSnackbar('Error loading items', 'error');
      console.error('Error loading items:', error);
    } finally {
      setLoading(false);
    }
  }, [shopName]);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleAddToCart = () => {
    if (!selectedItem) { showSnackbar('Please select an item', 'warning'); return; }
    if (quantity <= 0) { showSnackbar('Quantity must be greater than 0', 'warning'); return; }
    if (quantity > selectedItem.quantity) { showSnackbar(`Only ${selectedItem.quantity} units available`, 'error'); return; }

    let priceToUse = selectedItem.sellingPrice || 0;
    if (priceToUse <= 0) {
      const enteredPrice = parseFloat(sellingPrice);
      if (!enteredPrice || enteredPrice <= 0) { showSnackbar('Please enter a selling price for this item', 'error'); return; }
      priceToUse = enteredPrice;
    }

    const existingIndex = cart.findIndex(item => item.id === selectedItem.id);

    if (existingIndex >= 0) {
      const updatedCart = [...cart];
      const newQuantity = updatedCart[existingIndex].cartQuantity + quantity;
      if (newQuantity > selectedItem.quantity) { showSnackbar(`Total quantity exceeds available stock (${selectedItem.quantity})`, 'error'); return; }
      updatedCart[existingIndex].cartQuantity = newQuantity;
      updatedCart[existingIndex].subtotal = newQuantity * priceToUse;
      updatedCart[existingIndex].sellingPrice = priceToUse;
      setCart(updatedCart);
    } else {
      setCart([...cart, {
        ...selectedItem,
        cartQuantity: quantity,
        sellingPrice: priceToUse,
        subtotal: quantity * priceToUse
      }]);
    }

    setSelectedItem(null);
    setQuantity(1);
    setSellingPrice('');
    showSnackbar('Item added to cart', 'success');
  };

  const handleRemoveFromCart = (itemId) => {
    setCart(cart.filter(item => item.id !== itemId));
  };

  const handleUpdateCartQuantity = (itemId, newQuantity) => {
    if (newQuantity <= 0) { handleRemoveFromCart(itemId); return; }
    const updatedCart = cart.map(item => {
      if (item.id === itemId) {
        const originalItem = items.find(i => i.id === itemId);
        if (newQuantity > originalItem.quantity) { showSnackbar(`Only ${originalItem.quantity} units available`, 'error'); return item; }
        return { ...item, cartQuantity: newQuantity, subtotal: newQuantity * item.sellingPrice };
      }
      return item;
    });
    setCart(updatedCart);
  };

  const handleUpdateCartPrice = (itemId, newPrice) => {
    const price = parseFloat(newPrice) || 0;
    if (price <= 0) { showSnackbar('Selling price must be greater than 0', 'error'); return; }
    const updatedCart = cart.map(item => {
      if (item.id === itemId) { return { ...item, sellingPrice: price, subtotal: item.cartQuantity * price }; }
      return item;
    });
    setCart(updatedCart);
  };

  const calculateSubtotal = () => cart.reduce((sum, item) => sum + item.subtotal, 0);
  const calculateTotal = () => Math.max(0, calculateSubtotal() - (parseFloat(discountAmount) || 0));

  const handleCheckout = async () => {
    if (cart.length === 0) { showSnackbar('Cart is empty', 'warning'); return; }
    setProcessing(true);
    try {
      const freshItems = await inventoryService.getAllItems(shopName);
      const failedItems = [];
      const successfulItems = [];
      const totalDiscount = parseFloat(discountAmount) || 0;
      const cartSubtotal = calculateSubtotal();

      for (const cartItem of cart) {
        try {
          const latestItem = freshItems.find(i => i.id === cartItem.id);
          if (!latestItem) { failedItems.push({ name: cartItem.name, reason: 'Item not found' }); continue; }
          if (latestItem.quantity < cartItem.cartQuantity) { failedItems.push({ name: cartItem.name, reason: `Only ${latestItem.quantity} units available` }); continue; }
          const itemDiscount = cartSubtotal > 0 ? (cartItem.subtotal / cartSubtotal) * totalDiscount : 0;
          const priceToUse = cartItem.sellingPrice || latestItem.sellingPrice;
          await inventoryService.sellItem(latestItem, cartItem.cartQuantity, itemDiscount, priceToUse);
          successfulItems.push(cartItem.id);
        } catch (error) {
          console.error(`Error selling ${cartItem.name}:`, error);
          failedItems.push({ name: cartItem.name, reason: error.message || 'Unknown error' });
        }
      }

      await loadItems();

      if (failedItems.length > 0) {
        const failedNames = failedItems.map(f => f.name).join(', ');
        showSnackbar(`Some items failed to sell: ${failedNames}`, 'warning');
        setCart(cart.filter(item => !successfulItems.includes(item.id)));
      } else {
        const totalDisc = parseFloat(discountAmount) || 0;
        if (totalDisc > 0) {
          showSnackbar(`Successfully sold ${cart.length} item(s) with ${formatCurrency(totalDisc)} discount`, 'success');
        } else {
          showSnackbar(`Successfully sold ${cart.length} item(s)`, 'success');
        }
        setCart([]);
        setDiscountAmount(0);
      }
    } catch (error) {
      showSnackbar(`Error processing sale: ${error.message}`, 'error');
    } finally {
      setProcessing(false);
    }
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-PH', { style: 'currency', currency: 'PHP' }).format(amount);

  if (loading) {
    return (
      <Box sx={{ p: { xs: 3, sm: 4 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <SellIcon sx={{ fontSize: 28, color: '#F5F5F5' }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#F5F5F5' }}>Loading items...</Typography>
        </Box>
        <LinearProgress sx={{ borderRadius: 2, height: 4 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, pb: { xs: 10, sm: 3 } }}>
      {/* ── Header ── */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: { xs: 2.5, sm: 3 } }}>
        <Box sx={{
          width: 42, height: 42, borderRadius: 2.5,
          background: '#E53935',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 3px 10px rgba(229,57,53,0.3)',
        }}>
          <SellIcon sx={{ fontSize: 22, color: 'white' }} />
        </Box>
        <Box>
          <Typography sx={{
            fontWeight: 800, color: '#F5F5F5',
            fontSize: { xs: '1.15rem', sm: '1.5rem' },
            letterSpacing: '-0.02em',
          }}>
            Quick Sell
          </Typography>
          <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
            {shopName} • {items.length} items available
          </Typography>
        </Box>
      </Box>

      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: { xs: 2, sm: 3 },
      }}>
        {/* ── Left: Item Selection ── */}
        <Box sx={{
          borderRadius: 3, p: { xs: 2, sm: 3 },
          background: '#2D2D2D',
          border: '1px solid rgba(255,255,255,0.08)',
        }}>
          <Typography sx={{
            fontSize: { xs: '0.85rem', sm: '1rem' },
            fontWeight: 700, color: '#F5F5F5', mb: 2,
          }}>
            Select Item
          </Typography>

          <Autocomplete
            options={items}
            getOptionLabel={(option) => `${option.name} - ${option.brand} (${option.quantity} avail.)`}
            value={selectedItem}
            onChange={(event, newValue) => {
              setSelectedItem(newValue);
              if (newValue) { setQuantity(1); setSellingPrice(''); }
            }}
            renderInput={(params) => (
              <TextField {...params} label="Search items..." variant="outlined" fullWidth sx={{ '& .MuiInputBase-input': { fontSize: { xs: '16px', sm: '0.875rem' } } }} />
            )}
            sx={{ mb: 2.5 }}
          />

          {selectedItem && (
            <Box sx={{
              borderRadius: 2.5, p: 2.5, mb: 2.5,
              background: 'rgba(229,57,53,0.04)',
              border: '1px solid rgba(229,57,53,0.12)',
            }}>
              <Typography sx={{ fontWeight: 700, color: '#F5F5F5', fontSize: '1rem', mb: 1 }}>
                {selectedItem.name}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.8, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={selectedItem.brand} size="small" sx={{
                  fontWeight: 600, fontSize: '0.68rem',
                  background: 'rgba(255,255,255,0.08)', color: '#F5F5F5',
                }} />
                <Chip label={selectedItem.category} size="small" sx={{
                  fontWeight: 600, fontSize: '0.68rem',
                  background: 'rgba(229,57,53,0.1)', color: '#E53935',
                }} />
                <Chip label={`Stock: ${selectedItem.quantity}`} size="small" sx={{
                  fontWeight: 600, fontSize: '0.68rem',
                  background: selectedItem.quantity <= 5 ? 'rgba(245,158,11,0.12)' : 'rgba(16,185,129,0.1)',
                  color: selectedItem.quantity <= 5 ? '#d97706' : '#059669',
                }} />
                <Chip label={`Cost: ${formatCurrency(selectedItem.originalCost || 0)}`} size="small" sx={{
                  fontWeight: 600, fontSize: '0.68rem',
                  background: 'rgba(99,102,241,0.08)', color: '#6366f1',
                }} />
              </Box>
              {selectedItem.description && (
                <Typography sx={{ fontSize: '0.78rem', color: '#BDBDBD', mb: 1.5 }}>
                  {selectedItem.description}
                </Typography>
              )}
              {selectedItem.sellingPrice > 0 ? (
                <Box sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  background: 'rgba(16,185,129,0.1)', borderRadius: 2, px: 1.5, py: 0.5,
                }}>
                  <Typography sx={{ fontWeight: 800, color: '#059669', fontSize: '1.1rem' }}>
                    {formatCurrency(selectedItem.sellingPrice)}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#BDBDBD' }}>each</Typography>
                </Box>
              ) : (
                <TextField
                  label="Selling Price per Unit (₱)"
                  type="number"
                  value={sellingPrice}
                  onChange={(e) => setSellingPrice(e.target.value)}
                  fullWidth
                  required
                  size="small"
                  inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
                  helperText="This item has no selling price. Please enter one."
                  error={!sellingPrice || parseFloat(sellingPrice) <= 0}
                  sx={{ mt: 1 }}
                />
              )}
              {quantity > 0 && selectedItem.originalCost > 0 && (() => {
                const priceToUse = selectedItem.sellingPrice || parseFloat(sellingPrice) || 0;
                const totalCost = selectedItem.originalCost * quantity;
                const totalSale = priceToUse * quantity;
                const loss = totalCost - totalSale;
                if (loss > 0 && priceToUse > 0) {
                  return (
                    <Alert severity="warning" icon={<WarningIcon fontSize="small" />} sx={{ mt: 2, borderRadius: 2, fontSize: '0.75rem', py: 0.5 }}>
                      Loss of {formatCurrency(loss)} — Cost: {formatCurrency(totalCost)} &gt; Sale: {formatCurrency(totalSale)}
                    </Alert>
                  );
                }
                return null;
              })()}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2.5, flexWrap: 'wrap' }}>
            <TextField
              label="Quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1, max: selectedItem?.quantity || 1, style: { fontSize: '16px' } }}
              disabled={!selectedItem}
              sx={{ width: { xs: '100%', sm: 120 } }}
              size="small"
            />
            {selectedItem && (
              <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>
                Max: {selectedItem.quantity} units
              </Typography>
            )}
          </Box>

          <Button
            variant="contained"
            startIcon={<CartIcon />}
            onClick={handleAddToCart}
            disabled={
              !selectedItem || quantity <= 0 ||
              (selectedItem && (!selectedItem.sellingPrice || selectedItem.sellingPrice <= 0) && (!sellingPrice || parseFloat(sellingPrice) <= 0))
            }
            fullWidth
            size="large"
            sx={{
              background: '#E53935',
              fontWeight: 700, py: 1.5,
              '&:hover': {
                background: '#C62828',
                boxShadow: '0 6px 20px rgba(229,57,53,0.3)',
              },
            }}
          >
            Add to Cart
          </Button>
        </Box>

        {/* ── Right: Cart ── */}
        <Box sx={{
          borderRadius: 3, p: { xs: 2, sm: 3 },
          background: '#2D2D2D',
          border: '1px solid rgba(255,255,255,0.08)',
          display: 'flex', flexDirection: 'column',
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: { xs: '0.85rem', sm: '1rem' }, fontWeight: 700, color: '#F5F5F5' }}>
                Cart
              </Typography>
              {cart.length > 0 && (
                <Chip label={cart.length} size="small" sx={{
                  height: 22, fontWeight: 700, fontSize: '0.65rem',
                  background: '#E53935',
                  color: 'white',
                }} />
              )}
            </Box>
            {cart.length > 0 && (
              <Button
                size="small"
                onClick={() => { setCart([]); setDiscountAmount(0); }}
                sx={{ fontSize: '0.72rem', color: '#ef4444', fontWeight: 600 }}
              >
                Clear All
              </Button>
            )}
          </Box>

          {cart.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: { xs: 5, sm: 6 }, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              <EmptyCartIcon sx={{ fontSize: 52, color: 'rgba(255,255,255,0.2)', mb: 2 }} />
              <Typography sx={{ color: '#94a3b8', fontWeight: 600, fontSize: '0.9rem' }}>Cart is empty</Typography>
              <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', mt: 0.5 }}>Search and add items to get started</Typography>
            </Box>
          ) : (
            <>
              {/* Cart Items */}
              <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
                {cart.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <Box sx={{ py: 2, px: 0.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <Typography sx={{ fontWeight: 700, color: '#F5F5F5', fontSize: '0.88rem' }}>
                            {item.name}
                          </Typography>
                          <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8', mt: 0.3 }}>
                            {item.brand} • Stock: {item.quantity} • Cost: {formatCurrency(item.originalCost || 0)}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromCart(item.id)}
                          sx={{ color: '#ef4444', p: 0.5, minHeight: 44, minWidth: 44, '&:hover': { background: 'rgba(239,68,68,0.08)' } }}
                        >
                          <DeleteIcon sx={{ fontSize: '1.1rem' }} />
                        </IconButton>
                      </Box>

                      {/* Price Edit */}
                      <TextField
                        label="Price (₱)"
                        type="number"
                        value={item.sellingPrice}
                        onChange={(e) => handleUpdateCartPrice(item.id, e.target.value)}
                        size="small"
                        sx={{ maxWidth: { xs: '100%', sm: 180 }, mb: 1.5 }}
                        fullWidth
                        inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
                      />

                      {/* Loss Warning */}
                      {item.originalCost > 0 && item.sellingPrice > 0 && item.cartQuantity > 0 && (() => {
                        const totalCost = item.originalCost * item.cartQuantity;
                        const totalSale = item.sellingPrice * item.cartQuantity;
                        if (totalCost > totalSale) {
                          return (
                            <Alert severity="warning" sx={{ mb: 1, borderRadius: 2, fontSize: '0.68rem', py: 0.3 }}>
                              Loss of {formatCurrency(totalCost - totalSale)}
                            </Alert>
                          );
                        }
                        return null;
                      })()}

                      {/* Remaining */}
                      {(() => {
                        const originalItem = items.find(i => i.id === item.id);
                        const remaining = (originalItem ? originalItem.quantity : item.quantity) - item.cartQuantity;
                        return remaining < 0 ? (
                          <Typography sx={{ fontSize: '0.65rem', color: '#ef4444', fontWeight: 700, mb: 1 }}>
                            Exceeds stock!
                          </Typography>
                        ) : null;
                      })()}

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {/* Qty Controls */}
                        <Box sx={{
                          display: 'flex', alignItems: 'center', gap: 0,
                          border: '1px solid rgba(255,255,255,0.08)', borderRadius: 2,
                          overflow: 'hidden',
                        }}>
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateCartQuantity(item.id, item.cartQuantity - 1)}
                            sx={{ borderRadius: 0, px: 1, color: '#F5F5F5', minHeight: 44, minWidth: 44 }}
                          >
                            <RemoveIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <TextField
                            type="number"
                            value={item.cartQuantity}
                            onChange={(e) => handleUpdateCartQuantity(item.id, parseInt(e.target.value) || 0)}
                            inputProps={{ min: 1, max: item.quantity, style: { textAlign: 'center', padding: '4px 0', width: 40, fontSize: '0.8rem' } }}
                            variant="standard"
                            sx={{ '& .MuiInput-underline:before': { display: 'none' }, '& .MuiInput-underline:after': { display: 'none' } }}
                          />
                          <IconButton
                            size="small"
                            onClick={() => handleUpdateCartQuantity(item.id, item.cartQuantity + 1)}
                            disabled={item.cartQuantity >= item.quantity}
                            sx={{ borderRadius: 0, px: 1, color: '#F5F5F5', minHeight: 44, minWidth: 44 }}
                          >
                            <AddIcon sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                        </Box>

                        <Typography sx={{ fontWeight: 800, color: '#059669', fontSize: '1rem' }}>
                          {formatCurrency(item.subtotal)}
                        </Typography>
                      </Box>
                    </Box>
                    {index < cart.length - 1 && <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />}
                  </React.Fragment>
                ))}
              </Box>

              {/* Totals */}
              <Box sx={{
                borderRadius: 2.5, p: 2,
                background: 'rgba(229,57,53,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#BDBDBD' }}>Subtotal</Typography>
                  <Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{formatCurrency(calculateSubtotal())}</Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                  <Typography sx={{ fontSize: '0.82rem', color: '#BDBDBD' }}>Discount</Typography>
                  <TextField
                    type="number"
                    value={discountAmount}
                    onChange={(e) => setDiscountAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                    inputProps={{ min: 0, step: 0.01, style: { fontSize: '16px' } }}
                    sx={{ width: { xs: 120, sm: 100 } }}
                    size="small"
                    placeholder="0.00"
                  />
                </Box>

                <Divider sx={{ mb: 1.5, borderColor: 'rgba(255,255,255,0.08)' }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '1rem', fontWeight: 800, color: '#F5F5F5' }}>Total</Typography>
                  <Typography sx={{ fontSize: '1.3rem', fontWeight: 800, color: '#059669' }}>
                    {formatCurrency(calculateTotal())}
                  </Typography>
                </Box>
              </Box>

              <Button
                variant="contained"
                startIcon={processing ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                onClick={handleCheckout}
                disabled={processing || cart.length === 0}
                fullWidth
                size="large"
                sx={{
                  mt: 2, fontWeight: 700, py: 1.5,
                  background: '#E53935',
                  '&:hover': {
                    background: '#C62828',
                    boxShadow: '0 6px 20px rgba(229,57,53,0.3)',
                  },
                }}
              >
                {processing ? 'Processing...' : `Checkout — ${formatCurrency(calculateTotal())}`}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QuickSell;
